import type { ReactNode } from 'react'
import { RefreshCwIcon, SearchIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn, BRAND_MINT } from '@/lib/utils'

export interface DataListToolbarSearch {
  value: string
  onChange: (value: string) => void
  placeholder: string
  ariaLabel?: string
}

export interface DataListToolbarProps {
  /** Page title rendered as h1. */
  title: string
  /**
   * Line rendered under the title. Parent owns the logic for what to render
   * here — a `<span className="…animate-pulse…" />` skeleton, an empty-state
   * thesis, or a `{count} {noun}` sentence. Pass `undefined` to omit.
   */
  subtitle?: ReactNode
  /** Optional search field on the toolbar's left. */
  search?: DataListToolbarSearch
  /** Called when the refresh button is clicked. */
  onRefresh?: () => void
  /** Spinner state on the refresh button. */
  refreshing?: boolean
  /** Disables the refresh button while fetching. */
  loading?: boolean
  /** Right-aligned extras (e.g. an "Add employee" button). */
  actions?: ReactNode
  /**
   * Sets `aria-controls` on the toolbar wrapper when the toolbar proxies
   * controls for an adjacent card (e.g. search → filters the list below).
   */
  ariaControls?: string
}

/**
 * The chrome above a list: page title + dynamic subtitle (skeleton / empty /
 * count, all rendered by the parent), then a single action row with optional
 * search input, refresh button, and right-aligned extras.
 *
 * The action row collapses entirely when no interactive controls are
 * provided — so an `employee-settings`-style "header only" usage works
 * without rendering an empty flexbox.
 */
export function DataListToolbar({
  title,
  subtitle,
  search,
  onRefresh,
  refreshing,
  loading,
  actions,
  ariaControls,
}: DataListToolbarProps) {
  const hasActionRow = Boolean(search) || Boolean(onRefresh) || Boolean(actions)

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 shrink-0"
          style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
        />
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{title}</h1>
          {subtitle !== undefined && (
            <p className="text-muted-foreground text-sm">{subtitle}</p>
          )}
        </div>
      </div>
      {hasActionRow && (
        <div
          className="mt-4 flex flex-wrap items-center gap-3"
          aria-controls={ariaControls}
        >
          {search && (
            <div className="relative flex-1 min-w-[220px]">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                placeholder={search.placeholder}
                aria-label={search.ariaLabel ?? search.placeholder}
                className="pl-9"
              />
            </div>
          )}
          {onRefresh && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing || loading}
            >
              <RefreshCwIcon className={cn({ 'animate-spin': refreshing })} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          )}
          {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
        </div>
      )}
    </div>
  )
}
